<template>
  <div class="contents">
    <div class="container-fluid">
      <InvoicePageHeader :title="pageTitle">
        <template #actions>
          <router-link to="/invoice" class="btn px-15 btn-light border">
            <i class="fas fa-arrow-left fs-16"></i> Kembali
          </router-link>
        </template>
      </InvoicePageHeader>

      <div class="row">
        <div class="col-lg-8">
          <div class="inv-panel">
            <div class="inv-panel__head"><i class="fas fa-user"></i> Data customer</div>
            <div class="inv-panel__body">
              <div class="form-group">
                <label class="inv-form-label">Pilih customer tersimpan</label>
                <select v-model="form.customer_id" class="form-control" @change="onCustomerPick">
                  <option :value="null">— Isi manual —</option>
                  <option v-for="c in customers" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="row">
                <div class="col-md-6 form-group">
                  <label class="inv-form-label">Nama</label>
                  <input v-model="form.customer_name" class="form-control" placeholder="Nama customer">
                </div>
                <div class="col-md-6 form-group">
                  <label class="inv-form-label">WhatsApp</label>
                  <input v-model="form.customer_phone" class="form-control" placeholder="628…">
                </div>
                <div class="col-md-12 form-group mb-0">
                  <label class="inv-form-label">Email</label>
                  <input v-model="form.customer_email" type="email" class="form-control" placeholder="email@domain.com">
                </div>
              </div>
            </div>
          </div>

          <div class="inv-panel">
            <div class="inv-panel__head"><i class="fas fa-list"></i> Item invoice</div>
            <div class="inv-panel__body">
              <div v-for="(it, idx) in form.items" :key="idx" class="inv-item-row">
                <div class="row align-items-end">
                  <div class="col-md-5 form-group mb-md-0">
                    <label class="inv-form-label">Deskripsi</label>
                    <input v-model="it.description" class="form-control" placeholder="Nama produk / jasa">
                  </div>
                  <div class="col-4 col-md-2 form-group mb-md-0">
                    <label class="inv-form-label">Qty</label>
                    <input v-model.number="it.qty" type="number" min="1" class="form-control">
                  </div>
                  <div class="col-8 col-md-3 form-group mb-md-0">
                    <label class="inv-form-label">Harga satuan</label>
                    <input v-model.number="it.unit_price" type="number" min="0" class="form-control">
                  </div>
                  <div class="col-4 col-md-2 text-md-right">
                    <button type="button" class="btn btn-outline-danger btn-sm" :disabled="form.items.length <= 1" @click="form.items.splice(idx, 1)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div class="text-right mt-2 small text-muted">Subtotal: {{ formatRp(lineTotal(it)) }}</div>
              </div>
              <button type="button" class="btn btn-outline-primary btn-sm" @click="form.items.push({ description: '', qty: 1, unit_price: 0 })">
                <i class="fas fa-plus"></i> Tambah item
              </button>
            </div>
          </div>

          <div class="inv-panel">
            <div class="inv-panel__head"><i class="fas fa-sliders-h"></i> Pembayaran & catatan</div>
            <div class="inv-panel__body">
              <div class="row">
                <div class="col-md-4 form-group">
                  <label class="inv-form-label">Diskon (Rp)</label>
                  <input v-model.number="form.discount" type="number" min="0" class="form-control">
                </div>
                <div class="col-md-4 form-group">
                  <label class="inv-form-label">Pajak (%)</label>
                  <input v-model.number="form.tax_percent" type="number" min="0" class="form-control">
                </div>
                <div class="col-md-4 form-group">
                  <label class="inv-form-label">Metode bayar</label>
                  <select v-model="form.payment_method" class="form-control">
                    <option value="manual">Manual / transfer</option>
                    <option value="starqris">StarQRIS</option>
                  </select>
                </div>
              </div>
              <div class="form-group mb-0">
                <label class="inv-form-label">Catatan (opsional)</label>
                <textarea v-model="form.notes" class="form-control" rows="3" placeholder="Syarat pembayaran, rekening, dll."></textarea>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="inv-summary">
            <div class="small text-uppercase opacity-75 mb-2">Ringkasan</div>
            <div class="inv-summary__row"><span>Subtotal</span><span>{{ formatRp(subtotal) }}</span></div>
            <div class="inv-summary__row"><span>Diskon</span><span>− {{ formatRp(form.discount) }}</span></div>
            <div class="inv-summary__row"><span>Pajak ({{ form.tax_percent || 0 }}%)</span><span>{{ formatRp(taxAmount) }}</span></div>
            <div class="inv-summary__total d-flex justify-content-between align-items-center">
              <span>Total</span>
              <span>{{ formatRp(grandTotal) }}</span>
            </div>
            <button class="btn btn-light btn-block mt-4 font-weight-bold" :disabled="saving" @click="save">
              <span v-if="saving"><i class="fas fa-spinner fa-spin"></i> Menyimpan…</span>
              <span v-else><i class="fas fa-save"></i> Simpan invoice</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import InvoicePageHeader from '@/components/invoice/InvoicePageHeader.vue';
import { formatRp } from '@/utils/invoiceUi';
import '@/assets/invoice-module.css';

export default {
  components: { InvoicePageHeader },
  props: { id: { type: String, default: '' } },
  data() {
    return {
      saving: false,
      customers: [],
      form: {
        customer_id: null,
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        discount: 0,
        tax_percent: 0,
        payment_method: 'manual',
        notes: '',
        items: [{ description: '', qty: 1, unit_price: 0 }]
      }
    };
  },
  computed: {
    editId() {
      return this.id || this.$route.query.id || '';
    },
    pageTitle() {
      return this.editId ? 'Edit Invoice' : 'Buat Invoice';
    },
    subtotal() {
      return this.form.items.reduce((s, it) => s + this.lineTotal(it), 0);
    },
    taxAmount() {
      const base = Math.max(0, this.subtotal - Number(this.form.discount || 0));
      return Math.round(base * Number(this.form.tax_percent || 0) / 100);
    },
    grandTotal() {
      return Math.max(0, this.subtotal - Number(this.form.discount || 0) + this.taxAmount);
    }
  },
  mounted() {
    axios.get('invoice/customers').then(r => { this.customers = r.data.data.customers || []; });
    if (this.editId) {
      axios.get('invoice/invoices/' + this.editId).then(r => {
        const inv = r.data.data.invoice;
        this.form.customer_id = inv.customer_id;
        this.form.customer_name = inv.customer_name || '';
        this.form.customer_phone = inv.customer_phone || '';
        this.form.customer_email = inv.customer_email || '';
        this.form.discount = inv.discount;
        this.form.tax_percent = inv.tax_percent;
        this.form.payment_method = inv.payment_method || 'manual';
        this.form.notes = inv.notes || '';
        this.form.items = (inv.items || []).map(i => ({ description: i.description, qty: i.qty, unit_price: i.unit_price }));
        if (!this.form.items.length) {
          this.form.items = [{ description: '', qty: 1, unit_price: 0 }];
        }
      });
    }
  },
  methods: {
    formatRp,
    lineTotal(it) {
      return Number(it.qty || 0) * Number(it.unit_price || 0);
    },
    onCustomerPick() {
      const c = this.customers.find(x => x.id === this.form.customer_id);
      if (!c) return;
      this.form.customer_name = c.name;
      this.form.customer_phone = c.phone || '';
      this.form.customer_email = c.email || '';
    },
    save() {
      if (!this.form.customer_name.trim()) {
        this.$toast.error('Nama customer wajib diisi');
        return;
      }
      if (!this.form.items.some(it => (it.description || '').trim())) {
        this.$toast.error('Minimal satu item dengan deskripsi');
        return;
      }
      this.saving = true;
      const req = this.editId
        ? axios.put('invoice/invoices/' + this.editId, this.form)
        : axios.post('invoice/invoices', this.form);
      req.then(r => {
        this.$toast.success('Invoice tersimpan');
        this.$router.push('/invoice/' + (this.editId || r.data.data.invoice.id));
      }).catch(e => this.$toast.error(e.response?.data?.message || 'Gagal menyimpan'))
        .finally(() => { this.saving = false; });
    }
  }
};
</script>
